// @file Sentry client config
import { browserTracingIntegration, captureConsoleIntegration, init, replayIntegration } from '@sentry/astro'

// Exclusion patterns for Sentry events (simple string match)
const exclusionPatternsBasedOnMessage = [
  // See https://github.com/padlet/mozart/pull/38572
  'IMPORTANT! Eviction policy is volatile-lru. It should be "noeviction"',
  // KaTeX warns for unsupported Unicode characters
  // See https://linear.app/padlet/issue/TA-2273/sentry-no-character-metrics-for-2-in-style-main-regular-and-mode-text
  'No character metrics',
]

// Client-side network failures (offline, CORS, connection reset, etc.) — not bugs, avoid flooding Sentry
const networkErrorPatterns = [
  'failed to fetch',
  'networkerror',
  'network error',
  'load failed',
  'network request failed',
]

function shouldExcludeEventBasedOnMessage(message) {
  return exclusionPatternsBasedOnMessage.some((pattern) => message.includes(pattern))
}

function isNetworkErrorEvent(event) {
  const message = event.message ?? event.exception?.values?.[0]?.value ?? ''
  const msg = String(message).toLowerCase()
  return networkErrorPatterns.some((pattern) => msg.includes(pattern))
}

init({
  dsn: 'https://df73c90d4692118e144cce172118f75f@o253203.ingest.us.sentry.io/4508638862049280',
  enabled: process.env.BUILD_MODE !== 'development',
  environment: process.env.BUILD_MODE,

  // Drop events from E2E / test environments (e.g. arcade.padlet.test)
  // https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-denyurls
  denyUrls: [/^https?:\/\/[^\/]*\.test([\/?#:]|$)/],

  // Ignore errors from third-party browser extensions and in-app browsers
  // https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-ignoreerrors
  ignoreErrors: [
    // Snapchat in-app browser native bridge (SONDHEIM-19B)
    "Can't find variable: SCDynimacBridge",
    // Zalo in-app browser JS bridge (SONDHEIM-19B, SONDHEIM-1RS)
    /zaloJSV2/,
  ],

  integrations: [
    browserTracingIntegration(),
    replayIntegration(),
    captureConsoleIntegration({
      levels: ['warn', 'error'],
    }),
  ],

  // Define how likely traces are sampled. Adjust this value in production,
  // or use tracesSampler for greater control.
  tracesSampleRate: 0,

  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,

  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,

  beforeBreadcrumb(breadcrumb) {
    // Skip logging inline SVG images as breadcrumbs
    if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
      const url = breadcrumb.data?.url ?? ''
      if (url.startsWith('data:')) return null
    }
    return breadcrumb
  },

  beforeSend: (event, hint) => {
    if (isNetworkErrorEvent(event)) {
      return null
    }
    if (event.message && shouldExcludeEventBasedOnMessage(event.message)) {
      return null
    }
    return event
  },
})
